import useJumboAuth from '@jumbo/hooks/useJumboAuth';

// Define feature permissions mapping - which plans can access which features
const featurePermissions = {
  // Basic features
  uploadUtilityBills: ['residential', 'starter', 'professional', 'business'],
  energyStarIntegration: ['starter', 'professional', 'business'],
  portfolioAnalysis: ['starter', 'professional', 'business'],

  // Pro features
  complianceTracking: ['professional', 'business'],
  reportBuilder: ['professional', 'business'],
  complianceManagement: ['professional', 'business'],

  // Business features
  exportEmissionsReports: ['business'],
  realTimeMonitoring: ['business'],
  financialInsights: ['business'],
};

// Building limits by plan
export const buildingLimits = {
  residential: 1,
  starter: 15,
  professional: 75,
  business: 200,
};

export const useFeatureAccess = () => {
  const { authUser } = useJumboAuth();
  const organizationAccountType = authUser?.relationships?.organization?.data?.accountType || 'residential';
  const organizationAccountStatus = authUser?.relationships?.organization?.data?.accountStatus || 'active';
  /**
   * Check if the current user has access to a specific feature
   * @param featureKey - The feature key to check access for
   * @returns boolean indicating if the user has access
   */
  const hasAccess = (featureKey: keyof typeof featurePermissions): boolean => {
    // If the feature doesn't exist in our permissions map, deny access
    if (!featurePermissions[featureKey]) {
      return false;
    }

    // If the portfolio manager account is inactive, deny access to all features
    if (organizationAccountType !== 'residential' && organizationAccountStatus === 'inactive') {
      return false;
    }

    // Check if the user's plan is in the allowed plans for this feature
    return featurePermissions[featureKey].includes(organizationAccountType);
  };

  /**
   * Check if the user has reached their building limit
   * @param currentBuildingCount - The current number of buildings in the organization
   * @returns boolean indicating if the user has reached their limit
   */
  const hasReachedBuildingLimit = (currentBuildingCount: number): boolean => {
    // Get the maximum number of buildings allowed for the current plan
    const limit = buildingLimits[organizationAccountType as keyof typeof buildingLimits] || 1;

    // Compare the current building count with the plan limit
    // Note: This checks ALL projects in the organization, including both
    // parent projects and child properties, against the plan limit
    return currentBuildingCount >= limit;
  };

  /**
   * Get the maximum number of buildings allowed for the current plan
   * @returns number representing the building limit
   */
  const getBuildingLimit = (): number => {
    return buildingLimits[organizationAccountType as keyof typeof buildingLimits] || 1;
  };

  return {
    hasAccess,
    hasReachedBuildingLimit,
    getBuildingLimit,
    currentPlan: organizationAccountType,
    accountStatus: organizationAccountStatus,
  };
};

export default useFeatureAccess;
